import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useEffect, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import * as z from "zod"
import { BannerComponent } from "./BannerComponent.js";
import { Button, Typography } from "../../../../../ui";
import { LoadingButton } from "@mui/lab";
import { SelectOptions } from "./../SelectOptions";
import { SELECT_OPTIONS } from "./../configs";
import { useFormik, getIn } from "formik";
import SendIcon from "@mui/icons-material/Send";
import * as Yup from "yup";
import "./../myBanners.scss"
import { DOMAIN_API_HOST, DOMAIN_CONFIG } from "../../../../../configs/main"
import { RETARGEO_DOC_FILES, getRetargeoDocUrl } from "../../../../../configs/retargeoDocs"
import { useSnackbar } from "notistack";
import useAdminUser from "../../../../../data/useAdminUser";
import useUser from "../../../../../data/useUser";
import {
  DEFAULT_UTM_MARKERS,
  RETARGEO_UTM_MARKERS,
  NEW_UTM_TERM_REGEX,
  PRG_CODE_TEMPLATE,
  MARKUP_VERSION_LABELS,
  OUR_MACRO_ITEMS,
  UTM_MACRO_FIELDS,
  isAllowedUtmMarkersLayout,
  parseMarkupFields,
  buildMarkupString,
  templatesForVersion,
  markupVersionFromValue,
  inspectInternalUtm,
  composeLandingUrl,
  fillMarkupPreview,
} from "../../../../../utils/utmMarkers";

import {
  FormControl,
  FormHelperText,
  TextField,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { BuildTwoTone } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';


export const StyledHelperText = styled(FormHelperText)(({}) => ({
  background: "orange",
  width: "fit-content",
  position: "absolute",
  right: 0,
  top: 0,
  margin: 0,
  padding: "2px 5px",
  borderTopRightRadius: "5px",
}));

function MarkupLinkBox({ label, value, canCopy, isMobile }) {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: "#52525b", marginBottom: 6 }}>{label}</div>
      <div
        style={{
          position: "relative",
          padding: "10px 40px 10px 10px",
          background: "#f4f4f5",
          borderRadius: 6,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: isMobile ? 11 : 12,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          lineHeight: 1.5,
          minHeight: isMobile ? 64 : 88,
        }}
      >
        <IconButton
          size="small"
          disabled={!canCopy}
          onClick={() => {
            navigator.clipboard.writeText(value).then(() => {
              enqueueSnackbar("Ссылка скопирована", { variant: "success" });
            }).catch(() => {
              enqueueSnackbar("Не удалось скопировать ссылку", { variant: "error" });
            });
          }}
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            color: "#52525b",
          }}
          aria-label="Скопировать ссылку"
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {value}
      </div>
    </div>
  );
}

const MARKUP_FIELDS = [
  { name: "utm_source", label: "utm_source", hint: "Только Programmatic или Retargeo" },
  { name: "utm_medium", label: "utm_medium", hint: "" },
  { name: "utm_campaign", label: "utm_campaign", hint: "" },
  { name: "utm_content", label: "utm_content", hint: "" },
  { name: "utm_term", label: "utm_term", hint: "" },
  { name: "other", label: "other", hint: "Макросы сторонних систем, например {CLICK_ID}" },
];

function UtmMarkupForm({
  value,
  onChange,
  disabled,
  landingPage,
  campaignId,
  bannerId,
  error,
  isMobile,
  sourceDefault = "Programmatic",
}) {
  const lastEmitted = useRef(value);
  const activeFieldRef = useRef("utm_term");
  const caretRef = useRef({});
  const versionDrafts = useRef({});
  const [version, setVersion] = useState(() => markupVersionFromValue(value));
  const [fields, setFields] = useState(() => parseMarkupFields(value, sourceDefault));
  const [activeField, setActiveField] = useState("utm_term");
  const [checkResult, setCheckResult] = useState(null);

  const incomingValue = useRef(value);
  useEffect(() => {
    if (value === incomingValue.current) return;
    incomingValue.current = value;
    lastEmitted.current = value;
    versionDrafts.current = {};
    caretRef.current = {};
    setFields(parseMarkupFields(value, sourceDefault));
    setVersion(markupVersionFromValue(value));
  }, [value, sourceDefault]);

  const emit = (nextFields, nextVersion) => {
    const markers = buildMarkupString(nextFields, nextVersion);
    lastEmitted.current = markers;
    onChange(markers);
  };

  const changeField = (name, nextValue) => {
    const nextFields = { ...fields, [name]: nextValue };
    setFields(nextFields);
    emit(nextFields, version);
    setCheckResult(null);
  };

  const rememberCaret = (name, target) => {
    if (!target) return;
    caretRef.current[name] = target.selectionStart ?? String(fields[name] || "").length;
  };

  const focusField = (name) => {
    activeFieldRef.current = name;
    setActiveField(name);
  };

  const insertTargetField = () => {
    const currentTarget = activeFieldRef.current;
    if (UTM_MACRO_FIELDS.includes(currentTarget)) return currentTarget;
    return version === "v1" ? "utm_campaign" : "utm_term";
  };

  const insertMacro = (token) => {
    if (disabled) return;
    const target = insertTargetField();
    const current = fields[target] || "";
    if (current.includes(token)) return;
    const caret = caretRef.current[target];
    const pos = Number.isInteger(caret) ? caret : current.length;
    const leftPart = current.slice(0, pos).replace(/\s+$/, "");
    const rightPart = current.slice(pos).replace(/^\s+/, "");
    let piece = token;
    if (/\}$/.test(leftPart) && !leftPart.endsWith("/")) piece = `/${piece}`;
    if (/^\{/.test(rightPart) && !rightPart.startsWith("/")) piece = `${piece}/`;
    const nextValue = `${leftPart}${piece}${rightPart}`;
    caretRef.current[target] = leftPart.length + piece.length;
    focusField(target);
    changeField(target, nextValue);
  };

  const changeVersion = (nextVersion) => {
    if (disabled || nextVersion === version) return;
    versionDrafts.current[version] = { ...fields };
    const source = fields.utm_source || sourceDefault;
    const saved = versionDrafts.current[nextVersion];
    const nextFields = saved
      ? { ...saved, utm_source: source }
      : templatesForVersion(nextVersion, source);
    caretRef.current = {};
    setVersion(nextVersion);
    setFields(nextFields);
    emit(nextFields, nextVersion);
    setCheckResult(null);
  };

  const markers = buildMarkupString(fields, version);
  const urlWithMacros = composeLandingUrl(landingPage, markers);
  const urlFilled = composeLandingUrl(
    landingPage,
    fillMarkupPreview(markers, { campaignId, bannerId })
  );
  const idsReady = Boolean(campaignId && bannerId);

  return (
    <div
      style={{
        width: "100%",
        border: "1px solid #e4e4e7",
        borderRadius: 8,
        padding: isMobile ? 12 : 16,
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600 }}>Разметка трафика</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["v1", "v2", "v3"].map((item) => (
            <button
              key={item}
              type="button"
              disabled={disabled}
              onClick={() => changeVersion(item)}
              style={{
                border: item === version ? "1px solid #3F73F9" : "1px solid #e4e4e7",
                background: item === version ? "#eff4ff" : "#fff",
                color: item === version ? "#3F73F9" : "#52525b",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 12,
                fontWeight: 600,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              {item === "v1" ? "Версия 1" : item === "v2" ? "Версия 2" : "Версия 3"}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#52525b", marginBottom: 6 }}>
          Макросы — нажмите, чтобы вставить в {UTM_MACRO_FIELDS.includes(activeField) ? activeField : "utm-поле"}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {OUR_MACRO_ITEMS.map((macro) => {
            const target = UTM_MACRO_FIELDS.includes(activeField)
              ? activeField
              : version === "v1" ? "utm_campaign" : "utm_term";
            const alreadyInField = String(fields[target] || "").includes(macro.token);
            return (
            <button
              key={macro.token}
              type="button"
              disabled={disabled || alreadyInField}
              title={alreadyInField ? `${macro.label} уже стоит в ${target}` : macro.label}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertMacro(macro.token)}
              style={{
                border: "1px solid #d4d4d8",
                background: alreadyInField ? "#f4f4f5" : "#fafafa",
                color: alreadyInField ? "#a1a1aa" : "#18181b",
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 12,
                cursor: disabled || alreadyInField ? "not-allowed" : "pointer",
                fontFamily: "monospace",
              }}
            >
              {macro.token}
              <span style={{ marginLeft: 6, color: "#71717a", fontFamily: "inherit", fontSize: 11 }}>
                {macro.label}
              </span>
            </button>
            );
          })}
        </div>
        {version === "v3" ? (
          <div style={{ marginTop: 6, fontSize: 12, color: "#71717a" }}>
            В версии 3 макросы можно ставить в UTM. Статистика всё равно берётся из prg_code.
          </div>
        ) : (
          <div style={{ marginTop: 6, fontSize: 12, color: "#71717a" }}>
            В этой версии макросы ставятся прямо в UTM-метки.
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 12,
        }}
      >
        {MARKUP_FIELDS.map((field) => (
          <FormControl fullWidth key={field.name}>
            <TextField
              disabled={disabled}
              label={field.label}
              placeholder={field.hint}
              name={field.name}
              value={fields[field.name] || ""}
              onChange={(event) => {
                rememberCaret(field.name, event.target);
                changeField(field.name, event.target.value);
              }}
              onFocus={() => focusField(field.name)}
              onSelect={(event) => rememberCaret(field.name, event.target)}
              onClick={(event) => rememberCaret(field.name, event.target)}
              onKeyUp={(event) => rememberCaret(field.name, event.target)}
              size={isMobile ? "small" : "medium"}
            />
          </FormControl>
        ))}
        {version === "v3" ? (
          <FormControl fullWidth style={{ gridColumn: "1 / -1" }}>
            <TextField
              disabled
              label="prg_code"
              value={PRG_CODE_TEMPLATE}
              size={isMobile ? "small" : "medium"}
            />
          </FormControl>
        ) : null}
      </div>

      {version === "v3" ? (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 6,
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            color: "#9a3412",
            fontSize: isMobile ? 12 : 13,
            lineHeight: 1.45,
          }}
        >
          Не меняйте <b>prg_code</b> в конце ссылки ни при каких обстоятельствах и не правьте ссылку вручную — только через эту форму.
          Наши макросы можно дублировать в UTM, статистика всё равно идёт из prg_code. Макросы сторонних систем — в поле <b>other</b>.
        </div>
      ) : (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 6,
            background: "#f4f4f5",
            border: "1px solid #e4e4e7",
            color: "#52525b",
            fontSize: isMobile ? 12 : 13,
            lineHeight: 1.45,
          }}
        >
          Не правьте ссылку вручную — только через эту форму. Макросы сторонних систем добавляйте в поле <b>other</b>.
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 12,
          marginTop: 14,
        }}
      >
        <MarkupLinkBox
          label="Итоговая ссылка с макросами"
          value={urlWithMacros || "Укажите посадочную страницу"}
          canCopy={Boolean(urlWithMacros)}
          isMobile={isMobile}
        />
        <MarkupLinkBox
          label="Ссылка без макросов для сторонних систем"
          value={idsReady ? urlFilled : "После сохранения баннера подставятся UUID кампании и объявления"}
          canCopy={idsReady && Boolean(urlFilled)}
          isMobile={isMobile}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginTop: 14,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Button
          type="button"
          variant="primary"
          disabled={disabled}
          onClick={() => setCheckResult(inspectInternalUtm(markers))}
          style={{
            minWidth: isMobile ? 140 : 180,
            height: isMobile ? 36 : 40,
            fontSize: isMobile ? 13 : 14,
            fontWeight: 600,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          Проверить разметку
        </Button>
        <a
          href="/admin/memos/markup"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: isMobile ? 12 : 13,
            color: "#3F73F9",
            fontWeight: 600,
            textDecoration: "none",
            minHeight: 24,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Памятка: разметка
        </a>
        {error ? <span style={{ color: "#dc2626", fontSize: 12 }}>{error}</span> : null}
      </div>

      {checkResult ? (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 6,
            background: checkResult.ok ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${checkResult.ok ? "#bbf7d0" : "#fecaca"}`,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: checkResult.ok ? "#166534" : "#991b1b" }}>
            {checkResult.ok ? "Разметка корректна" : "Нужно исправить"}
            {checkResult.format ? ` · ${MARKUP_VERSION_LABELS[checkResult.format] || checkResult.format}` : ""}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
            {checkResult.issues.map((issue) => (
              <li key={issue.text} style={{ color: issue.tone === "bad" ? "#991b1b" : issue.tone === "ok" ? "#166534" : "#9a3412" }}>
                {issue.text}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}


const theme = (window.outerWidth > 1000) ? createTheme({}) : createTheme({
  components: {
    // Name of the component
    MuiTextField: {
      styleOverrides: {
        // Name of the slot
        root: {
          "& fieldset": { border: 'none' },
          // Some CSS
          // border: "0px solid white",
          marginTop: "-10px",
          marginBottom: "-15px",
        },
      },
      defaultProps: {
        inputProps: {
          // variant: 'standard',
          style: {
            fontSize: "12px",
            height: "0px",
            background: "#f0f1f3",
            border: "0px solid white",
            paddingTop: "15px",
          },
        },
        InputLabelProps : {
          style: { 
            fontSize: "12px",
            paddingTop: "10px",
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
          fontSize: '12px',
      }
  }
  },
});

const InitialValues = {
  utm_markers: DEFAULT_UTM_MARKERS,
  head: "",
  text: "",
  landing_page: "",
  call_to_action: "",
  event_trackers: [],
  click_trackers: [],
};

const PRO_PROGRAM_USER_IDS = ["e4022a68-6f05-4597-8f59-f195c33c2f9e", "7c3f2d6d-1e4e-4168-a0b9-b84664caeced"];
const PRO_PROGRAM_UTM_MARKERS = "utm_source=pro_program&utm_medium=cpc&utm_campaign=pro_program_RK&utm_content={PRG}&utm_term={CAMPAIGN_ID}/{BANNER_ID}/{SITE_ID}";

export const getDefaultUtmSource = (domain, userId) => {
  if (userId === "7c3f2d6d-1e4e-4168-a0b9-b84664caeced") {
    return PRO_PROGRAM_UTM_MARKERS;
  }
  if (domain === "lk.retargeo.ru") {
    return RETARGEO_UTM_MARKERS;
  }
  return DEFAULT_UTM_MARKERS;
};

const getInitialValues = (domain, userId) => ({
  utm_markers: getDefaultUtmSource(domain, userId),
  head: "",
  text: "",
  landing_page: "",
  call_to_action: "",
  event_trackers: [],
  click_trackers: [],
});

const validateTrackerUrl = (value) => {
  if (value == null || typeof value !== "string") return { ok: true };
  const s = value.trim();
  if (s === "") return value.length > 0 ? { ok: false, message: "Ссылка не должна состоять только из пробелов" } : { ok: true };
  if (!/^https?:\/\//i.test(s)) return { ok: false, message: "Ссылка должна начинаться с http:// или https://" };
  if (/\s/.test(s)) return { ok: false, message: "Ссылка не должна содержать пробелы" };
  const badPercent = s.match(/%(?![0-9A-Fa-f]{2})(?![a-zA-Z0-9_.]+%)[^%]*/);
  if (badPercent) {
    const pos = s.indexOf(badPercent[0]);
    const fragment = badPercent[0].length > 30 ? badPercent[0].slice(0, 30) + "…" : badPercent[0];
    return { ok: false, message: `Некорректный фрагмент «${fragment}» (с символа ${pos + 1}). Символ % допустим в формате %XX или в макросах вида %system.random%` };
  }
  const macroOnly = /^(%[a-zA-Z0-9_.]+%)*$/;
  const hasMacro = /%[a-zA-Z0-9_.]+%/;
  const qIdxOrig = s.indexOf("?");
  if (qIdxOrig !== -1) {
    const pairs = s.slice(qIdxOrig + 1).split("#")[0].split("&");
    for (const p of pairs) {
      const eq = p.indexOf("=");
      if (eq === -1) continue;
      const val = p.slice(eq + 1);
      if (hasMacro.test(val) && !macroOnly.test(val)) return { ok: false, message: "Введите корректную ссылку. Если в параметре указан макрос (например, %system.random%), значение должно состоять только из макроса." };
    }
  }
  const forParse = s.replace(/%[a-zA-Z0-9_.]+%/g, "%00");
  if (/[^\x00-\x7F]/.test(forParse)) return { ok: false, message: "Введите корректную ссылку. В параметрах допускаются только латиница, цифры и символы % & = ? и т.п." };
  const qIdx = forParse.indexOf("?");
  if (qIdx !== -1) {
    const query = forParse.slice(qIdx + 1).split("#")[0];
    if (/[^a-zA-Z0-9._~\-&=%]|%(?![0-9A-Fa-f]{2})/.test(query)) return { ok: false, message: "Введите корректную ссылку" };
  }
  if (!Yup.string().url().isValid(forParse)) return { ok: false, message: "Введите корректную ссылку" };
  try {
    const u = new URL(forParse);
    if (!["http:", "https:"].includes(u.protocol)) return { ok: false, message: "Ссылка должна использовать протокол http или https" };
    const host = (u.hostname || "").toLowerCase();
    if (host !== "localhost") {
      if (!/\./.test(host)) return { ok: false, message: "Укажите ссылку с доменом (например, example.com или amc.yandex.ru)" };
      if (!/^[a-z0-9.-]+$/i.test(host) || /^\.|\.$|\.\.|^-|-$/.test(host)) return { ok: false, message: "Введите корректную ссылку" };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, message: "Введите корректную ссылку" };
  }
};


const LOCK_MSG_ACTIVE = "Нельзя редактировать: объявление включено. Сначала выключите его";
const LOCK_MSG_IMPRESSIONS = "Нельзя редактировать: у баннеров объявления уже были показы";
const LOCK_MSG_IMPRESSIONS_SUPERUSER = "У баннеров объявления уже были показы (для superuser можно)";
const LOCK_MSG_MEDIA_PLAN = "Нельзя редактировать: дата окончания медиаплана уже прошла. Обновите медиаплан в настройках кампании";

export const getAdEditLockState = ({ mediaPlanExpired, status, hasImpressions, isSuperuser = false }) => {
  if (hasImpressions && !isSuperuser) {
    return { locked: true, message: LOCK_MSG_IMPRESSIONS };
  }
  if (status === "ACTIVE") {
    return { locked: true, message: LOCK_MSG_ACTIVE };
  }
  if (mediaPlanExpired) {
    return { locked: true, message: LOCK_MSG_MEDIA_PLAN };
  }
  return { locked: false, message: "" };
};
const LEGACY_NO_IMPRESSIONS_LOCK_DOMAIN = "lk.programmatic.ru";
const RETARGEO_DOMAIN = "lk.retargeo.ru";
const HEAD_MAX_LENGTH = 56;
const TEXT_MAX_LENGTH = 81;

const RETARGEO_BANNER_SPECS = [
  { size: "240×400", withButton: true, maxSize: "120 КБ" },
  { size: "300×500", withButton: true, maxSize: "120 КБ" },
  { size: "300×600", withButton: true, maxSize: "120 КБ" },
  { size: "300×250", withButton: true, maxSize: "120 КБ" },
  { size: "728×90", withButton: true, maxSize: "120 КБ" },
  { size: "1080×607", withButton: false, maxSize: "2 МБ" },
  { size: "600×600", withButton: false, maxSize: "2 МБ" },
  { size: "450×450", withButton: false, maxSize: "2 МБ" },
];

const retargeoActionButtonStyle = (isMobile) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: isMobile ? "7px 12px" : "8px 14px",
  borderRadius: 6,
  fontSize: isMobile ? 12 : 13,
  fontWeight: 600,
  color: "#3F73F9",
  background: "#DFE8FF",
  textDecoration: "none",
  whiteSpace: isMobile ? "normal" : "nowrap",
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1.3,
  transition: "background 0.15s ease",
});

const RetargeoSpecChip = ({ spec, isMobile }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: isMobile ? "6px 10px" : "7px 12px",
      borderRadius: 8,
      background: "#fff",
      border: "1px solid #e0e7ff",
      boxShadow: "0 1px 2px rgba(63, 115, 249, 0.05)",
    }}
  >
    <span
      style={{
        fontWeight: 700,
        fontSize: isMobile ? 12 : 13,
        color: "#18181b",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {spec.size}
    </span>
    <span
      style={{
        width: 1,
        height: 14,
        background: "#e4e4e7",
        flexShrink: 0,
      }}
    />
  </div>
);

const RetargeoSpecGroup = ({ title, hint, limitHint, specs, isMobile }) => (
  <div
    style={{
      padding: isMobile ? 10 : 12,
      borderRadius: 8,
      background: "#fff",
      border: "1px solid #e8eeff",
    }}
  >
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
      <span style={{ fontWeight: 700, fontSize: isMobile ? 12 : 13, color: "#18181b" }}>{title}</span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "#3F73F9",
          background: "#eef3ff",
          padding: "2px 8px",
          borderRadius: 20,
          whiteSpace: "nowrap",
        }}
      >
        {limitHint}
      </span>
    </div>
    <div style={{ fontSize: isMobile ? 11 : 12, color: "#71717a", marginBottom: 10 }}>{hint}</div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? 6 : 8 }}>
      {specs.map((spec) => (
        <RetargeoSpecChip key={spec.size} spec={spec} isMobile={isMobile} />
      ))}
    </div>
  </div>
);

const RetargeoBannerSpecs = ({ isMobile }) => {
  const [rulesOpen, setRulesOpen] = useState(false);
  const { user } = useUser();
  const rulesUrl = getRetargeoDocUrl(RETARGEO_DOC_FILES.adsRules, user);
  const withButtonSpecs = RETARGEO_BANNER_SPECS.filter((s) => s.withButton);
  const withoutButtonSpecs = RETARGEO_BANNER_SPECS.filter((s) => !s.withButton);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: isMobile ? 10 : 12,
        }}
      >
        <button
          type="button"
          style={retargeoActionButtonStyle(isMobile)}
          onClick={() => setRulesOpen(true)}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#cdd9ff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#DFE8FF"; }}
        >
          Правила размещения рекламных объявлений
        </button>
      </div>

      <Dialog
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
            pb: 1,
            pt: isMobile ? 2 : 2.5,
            pl: isMobile ? 2 : 3,
            fontSize: isMobile ? 17 : 18,
            fontWeight: 600,
            color: "#18181b",
          }}
        >
          Требования к креативам
          <IconButton
            onClick={() => setRulesOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="Закрыть"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 1 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 10 : 12 }}>
            <RetargeoSpecGroup
              title="С кнопкой"
              hint="На баннере отображаются заголовок, описание и текст кнопки"
              limitHint="до 120 КБ"
              specs={withButtonSpecs}
              isMobile={isMobile}
            />
            <RetargeoSpecGroup
              title="Без кнопки"
              hint="Только изображение, без текста и кнопки"
              limitHint="до 2 МБ"
              specs={withoutButtonSpecs}
              isMobile={isMobile}
            />
          </div>
          <div style={{ marginTop: isMobile ? 16 : 20, paddingTop: isMobile ? 14 : 16, borderTop: "1px solid #e8eeff" }}>
            <a
              href={rulesUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...retargeoActionButtonStyle(isMobile),
                display: "inline-flex",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#cdd9ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#DFE8FF"; }}
            >
              Правила размещения рекламных объявлений
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const isImpressionsLocked = (hasImpressions, signupDomain) =>
  Boolean(hasImpressions) && signupDomain !== LEGACY_NO_IMPRESSIONS_LOCK_DOMAIN;

const normalizeCallToActionKey = (value) => {
  if (!value) return "";
  if (SELECT_OPTIONS[value]) return value;
  const byLabel = Object.entries(SELECT_OPTIONS).find(([, label]) => label === value);
  if (byLabel) return byLabel[0];
  const normalized = String(value).toLowerCase();
  const byKey = Object.keys(SELECT_OPTIONS).find((key) => key.toLowerCase() === normalized);
  return byKey || "";
};

export const Banners = ({ campaignId, currentAdId, changeTab, disable, mediaPlanExpired = false, upd, setUpd, updBanner, setUpdBanner, setHasUnsavedChanges, saveFormRef, isMobile = false, hideNextButton = false, inModal = false, onClose = null }) => {
  const [isSubmitting , setIsSubmitting ] = useState(false);
  const [banners, setBanners] = useState([]);
  const [hasImpressions, setHasImpressions] = useState(false);
  const hasImpressionsRef = useRef(false);
  const [data, setData] = useState(InitialValues);
  const [signupDomain, setSignupDomain] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [markupDraft, setMarkupDraft] = useState("");
  const markupDraftRef = useRef("");
  const { enqueueSnackbar } = useSnackbar();
  const { role } = useAdminUser();
  const { user } = useUser();
  const isMarkupEditor = ["MANAGER", "ADMIN", "SUPERUSER"].some((el) => el === role);
  const isSuperuser = role === "SUPERUSER";
  const isSuperuserRef = useRef(false);
  isSuperuserRef.current = isSuperuser;
  const superuserAuthHeaders = (() => {
    if (!isSuperuser) return {};
    const adminToken = localStorage.getItem("admin_token");
    return adminToken ? { "X-Admin-Token": adminToken } : {};
  })();

  const validationSchema = Yup.object().shape({
    utm_markers: Yup.string()
      .test(
        "utm-markers-rules",
        "Некорректный формат UTM метки",
        function (value) {
          if (!value) return true;
          const isLowercaseProgrammaticUser = user?.id === "465b755a-aa09-44f2-93cc-0368ac7cfda8";
          const programmaticSourcePattern = isLowercaseProgrammaticUser ? "Programmatic|programmatic" : "Programmatic";
          const defaultRegex = new RegExp(`^utm_source=(?:${programmaticSourcePattern}|Retargeo)(?:&utm_[a-zA-Z0-9]+=[^&]*)*$`);
          const coopAllowedRegex = /^utm_source=(?:Programmatic|Retargeo|coop_programmatic)(&utm_(?:medium|campaign|content|term)=(?:{(?:PRG|CAMPAIGN_ID|BANNER_ID|SITE_ID)}|[^&{}]+))*$/;
          const isStrictUser = ["9328b7ef-1bc3-4d68-b310-2995d19f140f"].includes(user?.id);
          const isCoopUser = user?.id === "4995ae98-4797-43db-ba54-45a6613dfc7b";
          const isProProgramUser = PRO_PROGRAM_USER_IDS.includes(user?.id);
          const hasUtmTerm = /[&]?utm_term=/.test(value);

          if (value.startsWith("utm_source=pro_program")) {
            const parts = value.split("&");
            const termPart = parts.find((p) => p.startsWith("utm_term="));
            if (!termPart) return true;
            const termValue = termPart.slice("utm_term=".length);
            return NEW_UTM_TERM_REGEX.test(termValue);
          }

          if (isAllowedUtmMarkersLayout(value, programmaticSourcePattern)) return true;

          if (isProProgramUser) return false;

          if (!isStrictUser && !isCoopUser) {
            return false;
          }

          if (isCoopUser) {
            if (!hasUtmTerm) return coopAllowedRegex.test(value);
            const strictCoopRegex = /^utm_source=(?:Programmatic|coop_programmatic)(?:&utm_medium=[^&]+)?(?:&utm_campaign=[^&]+)?(?:&utm_content=[^&]+)?&utm_term=\{CAMPAIGN_ID\}\/\{BANNER_ID\}(?:\/\{PRG\}(?:\/\{SITE_ID\})?)?$/;
            return strictCoopRegex.test(value);
          }

          if (isStrictUser) {
            if (!hasUtmTerm) return defaultRegex.test(value);
            const strictSpecialRegex = /^utm_source=Programmatic(?:&utm_medium=[^&]+)?(?:&utm_campaign=[^&]+)?(?:&utm_content=[^&]+)?&utm_term=\{CAMPAIGN_ID\}\/\{BANNER_ID\}(?:\/\{PRG\}(?:\/\{SITE_ID\})?)?$/;
            return strictSpecialRegex.test(value);
          }

          return defaultRegex.test(value);
        }
      ),
    landing_page: Yup.string().required("Введите ссылку").url("Пример корректной ссылки: https://example.com"),
    head: Yup.string().required("Введите заголовок").max(HEAD_MAX_LENGTH, `Заголовок не может быть больше ${HEAD_MAX_LENGTH} символов`),
    text: Yup.string().required("Введите описание").max(TEXT_MAX_LENGTH, `Описание не может быть длиннее ${TEXT_MAX_LENGTH} символов`),
    text_utm: Yup.string(),
    call_to_action: Yup.string().required(),
    event_trackers: Yup.array().of(
      Yup.string().test("tracker-url", function (v) {
        const result = validateTrackerUrl(v);
        return result.ok ? true : this.createError({ message: result.message });
      })
    ).max(10, "Максимум 10 трекеров показов"),
    click_trackers: Yup.array().of(
      Yup.string().test("tracker-url", function (v) {
        const result = validateTrackerUrl(v);
        return result.ok ? true : this.createError({ message: result.message });
      })
    ).max(10, "Максимум 10 трекеров кликов"),
  });

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append(
        "Authorization",
        "Bearer " + localStorage.getItem("token")
      )
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/get?_id=${currentAdId}`, {
        method: "GET",
        headers: myHeaders,
      });
      if (cancelled) return;
      if (response.ok) {
        const result = await response.json();
        let mergedData
        
        if (result.signup_domain) {
          setSignupDomain(result.signup_domain);
        }
        
        const signupDomain = result.signup_domain || "lk.programmatic.ru";
        let defaultUtmMarkers = getDefaultUtmSource(signupDomain, user?.id);
        if (result.signup_domain && !result.utm_markers) {
          defaultUtmMarkers = getDefaultUtmSource(result.signup_domain, user?.id);
        }
        
        mergedData = {
          ...getInitialValues(signupDomain, user?.id),
          ...result,
          utm_markers: result.utm_markers || defaultUtmMarkers,
          call_to_action: normalizeCallToActionKey(result.call_to_action),
        };
        
        setData(mergedData);
        markupDraftRef.current = mergedData.utm_markers || "";
        setMarkupDraft(mergedData.utm_markers || "");
        setBanners(result.banners);
        const impressionsLocked = isImpressionsLocked(
          result.has_impressions,
          result.signup_domain
        );
        hasImpressionsRef.current = impressionsLocked;
        setHasImpressions(impressionsLocked);
        setIsInitialized(true);
        
        console.log("banners", mergedData)
      }
    }
    if (!currentAdId) {
      hasImpressionsRef.current = false;
      setHasImpressions(false);
      return undefined;
    }
    hasImpressionsRef.current = false;
    setHasImpressions(false);
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [currentAdId, upd, user?.id])

  const deleteAd = async (adId) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("token")
    )
    const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/delete?_id=${adId}`, {
      method: "POST",
      body: JSON.stringify({}),
      headers: myHeaders,
    });
    if (response.ok) {
      const data = await response.json();
      return data
    }
  }
  
  const values = useFormik({
      initialValues: data,
      validationSchema: validationSchema,
      enableReinitialize: true,
      onSubmit: async (values, { resetForm }) => {
        if (JSON.stringify(values) === JSON.stringify(data)) return;
        const submitLock = getAdEditLockState({
          mediaPlanExpired,
          status: values.status,
          hasImpressions: hasImpressionsRef.current,
          isSuperuser: isSuperuserRef.current,
        });
        if (submitLock.locked) {
          enqueueSnackbar(submitLock.message, { variant: "info" });
          return;
        }

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append(
          "Authorization",
          "Bearer " + localStorage.getItem("token")
        );
        if (isSuperuserRef.current) {
          const adminToken = localStorage.getItem("admin_token");
          if (adminToken) myHeaders.append("X-Admin-Token", adminToken);
        }
        try {
          const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/update?_id=${currentAdId}`, {
            method: "POST",
            body: JSON.stringify({
              head: values.head,
              text: values.text,
              call_to_action: values.call_to_action,
              landing_page: values.landing_page,
              utm_markers: isMarkupEditor ? (markupDraftRef.current || values.utm_markers) : values.utm_markers,
              event_trackers: values.event_trackers,
              click_trackers: values.click_trackers
            }),
            headers: myHeaders,
          });
          if (response.ok) {
            setUpd((value) => !value);
            enqueueSnackbar("Объявление успешно сохранено", { variant: "success" });

            resetForm({ values: values });
          } else {
            let errorMessage = "Ошибка при сохранении объявления";
            try {
              const errorData = await response.json();
              if (errorData?.detail) errorMessage = String(errorData.detail);
            } catch (_) {}
            enqueueSnackbar(errorMessage, { variant: "error" });

            resetForm({ values: values });
          }
        } catch (error) {
          console.error(error);
        }

      return;
      },
    });

  const editLock = getAdEditLockState({
    mediaPlanExpired,
    status: values.values.status,
    hasImpressions,
    isSuperuser,
  });
  const isContentLocked = editLock.locked;
  const lockMessage = editLock.message;
  const badgeMessage = isContentLocked
    ? lockMessage
    : (hasImpressions && isSuperuser ? LOCK_MSG_IMPRESSIONS_SUPERUSER : "");
  const showEditBadge = Boolean(badgeMessage);

  const submitAdvertisement = async () => {
    if (isMarkupEditor) {
      await values.setFieldValue("utm_markers", markupDraftRef.current || values.values.utm_markers);
    }
    await values.submitForm();
  };

  useEffect(() => {
    saveFormRef.current = async (adId) => {
      if (isMarkupEditor) {
        await values.setFieldValue("utm_markers", markupDraftRef.current || values.values.utm_markers);
      }
      await values.submitForm();
      return adId;
    };
  }, [values.submitForm, values.setFieldValue, saveFormRef, values.values.utm_markers, isMarkupEditor]);

  useEffect(() => {
    if (!isInitialized) return;
    const current = isMarkupEditor
      ? { ...values.values, utm_markers: markupDraft }
      : values.values;
    setHasUnsavedChanges(JSON.stringify(current) !== JSON.stringify(data));
  }, [values.values, data, setHasUnsavedChanges, isInitialized, markupDraft, isMarkupEditor]);

  useEffect(() => {
    if (isInitialized && data && Object.keys(data).length > 0) {
      values.setValues(data);
    }
  }, [isInitialized, data]);

  const wrapperStyle = inModal
    ? { width: "100%", padding: 0 }
    : isMobile
      ? { padding: "20px", width: "100%" }
      : (window.outerWidth > 1000)
        ? { marginLeft: "20px", marginTop: "30px" }
        : (window.outerWidth < 375)
          ? { width: "288px", marginLeft: "20px", marginTop: "40px" }
          : (window.outerWidth < 768)
            ? { width: "343px", marginLeft: "20px", marginTop: "40px" }
            : {};

  return (
    <div style={wrapperStyle}>
      {!inModal && !currentAdId && (
        <div
          style={{
            position: "absolute",
            top: isMobile ? 300 : 150,
            left: 0,
            width: "100%",
            height: isMobile ? "100%" : "89%",
            background: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "bold",
              color: "#333",
              textAlign: "center",
              padding: isMobile ? "0 20px" : 0
            }}
          >
            {"Создайте объявление выше"}
          </div>
        </div>
      )}
      {!inModal && (
        <div className="title-text" style={{ marginBottom: isMobile ? "0px" : "50px" }}>
          <h2 style={{ fontSize: isMobile ? "20px" : "24px" }}>Объявления</h2>
        </div>
      )}

      <Stack marginBottom={isMobile ? "20px" : "36px"}>
        {showEditBadge && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              fontSize: isMobile ? 12 : 13,
              color: "#9a3412",
              lineHeight: 1.45,
            }}
          >
            {badgeMessage}
          </div>
        )}
        {signupDomain === RETARGEO_DOMAIN && (
          <RetargeoBannerSpecs isMobile={isMobile} />
        )}
        <div className="banner_load_wrap">
          <BannerComponent 
            campaign_id={campaignId} 
            upd={upd} 
            setUpd={setUpd} 
            updBanner={updBanner} 
            setUpdBanner={setUpdBanner} 
            currentAdId={currentAdId} 
            banners={banners} 
            setBanners={setBanners} 
            disabled={isContentLocked}
            lockMessage={lockMessage}
            authExtraHeaders={superuserAuthHeaders}
            isMobile={isMobile}
            signupDomain={signupDomain}
          />
        </div>
      </Stack>
      
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submitAdvertisement();
        }}
      >
        <div style={{ display: "flex", gap: "20px", flexDirection: "column" }}>
          <div style={{ 
            display: "flex", 
            gap: isMobile ? "20px" : "30px", 
            justifyContent: "space-between",
            flexDirection: isMobile ? "column" : "row"
          }}>
            <div style={{ width: "100%" }}>
              <Stack style={{ width: "100%" }} marginTop={(window.outerWidth > 1000) ? "0px" : "-30px"}>
              <Typography variant="h1" className="mb_2" style={{ fontSize: isMobile ? "16px" : undefined }}>
                Текст объявления
              </Typography>
              </Stack>
              <Stack
                direction={(window.outerWidth > 767) ? "row" : "column"}
                justifyContent={(window.outerWidth > 767) ? "space-between" : "flex-start"}
                marginBottom={(window.outerWidth > 1000) ? "34px" : "0px"}
                gap={(window.outerWidth > 767) ? "40px" : "10px"}
              >
                <Stack style={{ width: "100%" }}> 
                  <Stack spacing={(window.outerWidth > 767) ? "16px" : "2px"} className="mb_3">
                    <Stack spacing={(window.outerWidth > 1000) ? "16px" : "5px"} className="mb_3">
                    <ThemeProvider theme={theme}>
                    
                      <div style={{ position: "relative" }}>
                        {isContentLocked && (
                          <div
                            onClick={() => enqueueSnackbar(lockMessage, { variant: "info" })}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              background: "transparent",
                              cursor: "not-allowed",
                              zIndex: 10,
                            }}
                          />
                        )}
                        <FormControl fullWidth>
                          <TextField
                            disabled={isContentLocked}
                            label={
                              window.outerWidth > 1000
                                ? signupDomain === RETARGEO_DOMAIN
                                  ? `Заголовок (допустимое количество символов: ${HEAD_MAX_LENGTH})`
                                  : "Заголовок"
                                : ""
                            }
                            placeholder={
                              window.outerWidth > 1000
                                ? ""
                                : signupDomain === RETARGEO_DOMAIN
                                  ? `Заголовок (${HEAD_MAX_LENGTH})`
                                  : "Заголовок"
                            }
                            name="head"
                            onChange={values.handleChange}
                            value={values.values.head}
                            error={values.values.head.length > HEAD_MAX_LENGTH}
                            helperText={
                              values.values.head.length > HEAD_MAX_LENGTH
                                ? `Заголовок не может быть больше ${HEAD_MAX_LENGTH} символов`
                                : signupDomain === RETARGEO_DOMAIN
                                  ? `${values.values.head.length}/${HEAD_MAX_LENGTH}`
                                  : <br />
                            }
                            size={isMobile ? "small" : "medium"}
                          />
                          {Boolean(values.touched.head && values.errors.head) && (
                            <StyledHelperText>{values.errors.head}</StyledHelperText>
                          )}
                        </FormControl>
                      </div>
                    </ThemeProvider>
                    <div style={{ position: "relative" }}>
                      {isContentLocked && (
                        <div
                          onClick={() => enqueueSnackbar(lockMessage, { variant: "info" })}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "transparent",
                            cursor: "not-allowed",
                            zIndex: 10,
                          }}
                        />
                      )}
                      <FormControl fullWidth>
                        <TextField
                          disabled={isContentLocked}
                          label={
                            window.outerWidth > 1000
                              ? signupDomain === RETARGEO_DOMAIN
                                ? `Описание (допустимое количество символов: ${TEXT_MAX_LENGTH})`
                                : "Описание"
                              : ""
                          }
                          placeholder={
                            window.outerWidth > 1000
                              ? ""
                              : signupDomain === RETARGEO_DOMAIN
                                ? `Описание (${TEXT_MAX_LENGTH})`
                                : "Описание"
                          }
                          name="text"
                          value={values.values.text}
                          multiline={true}
                          rows={isMobile ? 3 : 5}
                          inputProps={(window.outerWidth > 1000) ? {} : {style: {fontSize: "12px"}}}
                          InputLabelProps={(window.outerWidth > 1000) ? {} : {style: {fontSize: "12px"}}}
                          sx = {(window.outerWidth > 1000) ? {} : {background: "#f0f1f3", "& fieldset": { border: 'none' }, borderRadius: "5px"}}
                          onChange={values.handleChange}
                          error={values.values.text.length > TEXT_MAX_LENGTH}
                          helperText={
                            values.values.text.length > TEXT_MAX_LENGTH
                              ? `Описание не может быть длиннее ${TEXT_MAX_LENGTH} символов`
                              : signupDomain === RETARGEO_DOMAIN
                                ? `${values.values.text.length}/${TEXT_MAX_LENGTH}`
                                : <br />
                          }
                          size={isMobile ? "small" : "medium"}
                        />
                        {Boolean(values.touched.text && values.errors.text) && (
                          <StyledHelperText>{values.errors.text}</StyledHelperText>
                        )}
                      </FormControl>
                    </div>

                    <SelectOptions
                        disable={isContentLocked}
                        name="call_to_action"
                        options={SELECT_OPTIONS}
                        value={SELECT_OPTIONS[values.values.call_to_action] || ""}
                        title="Выберите текст кнопки:"
                        onChange={(label) => {
                          const key = Object.entries(SELECT_OPTIONS).find(([, v]) => v === label)?.[0];
                          if (key) values.setFieldValue("call_to_action", key);
                        }}
                        isMobile={isMobile}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </div>

            <Stack style={{ width: "100%" }} className="mb_3">
              <Typography variant="h1" className="mb_2" style={{ fontSize: isMobile ? "16px" : undefined }}>
                Посадочная страница / UTM метки
              </Typography>

              <Stack
                direction={"column"}
                justifyContent={(window.outerWidth > 767) ? "space-between" : "flex-start"}
                marginBottom={(window.outerWidth > 1000) ? "34px" : "0px"}
                gap={(window.outerWidth > 767) ? "40px" : "10px"}
              >
                <div style={{ position: "relative" }}>
                  {isContentLocked && (
                    <div
                      onClick={() => enqueueSnackbar(lockMessage, { variant: "info" })}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "transparent",
                        cursor: "not-allowed",
                        zIndex: 10,
                      }}
                    />
                  )}
                  <FormControl fullWidth>
                    <TextField
                      disabled={isContentLocked}
                      label="Посадочная страница"
                      placeholder="https://example.com"
                      name="landing_page"
                      onChange={values.handleChange}
                      value={values.values.landing_page}
                      onBlur={() => {
                        values.setFieldValue(
                          "landing_page",
                          (() => {
                            const url = new URL(values.values.landing_page);
                            const params = new URLSearchParams(url.search);
                            const hasUtmParams = Array.from(params.keys()).some(
                              (key) => key.startsWith("utm_") || key === "prg_code" || key === "other"
                            );
                            
                            if (hasUtmParams) {
                              enqueueSnackbar("Нельзя использовать UTM параметры в ссылке, они будут заменены метками сервиса.", {
                                variant: "warning",
                                anchorOrigin: { vertical: "top", horizontal: "right" }
                              });
                              
                              Array.from(params.keys())
                                .filter((key) => key.startsWith("utm_") || key === "prg_code" || key === "other")
                                .forEach((key) => params.delete(key));
                                
                              url.search = params.toString();
                              return url.toString();
                            }
                            
                            return values.values.landing_page;
                          })()
                        );
                      }}
                      size={isMobile ? "small" : "medium"}
                    />
                    {Boolean(values.touched.landing_page && values.errors.landing_page) && (
                      <StyledHelperText>{values.errors.landing_page}</StyledHelperText>
                    )}
                  </FormControl>
                </div>
                {!isMarkupEditor && (
                <FormControl fullWidth>
                  <TextField
                    disabled={ !["ADMIN", "SUPERUSER"].some((el) => (el === role)) || (isContentLocked)}
                    label={(window.outerWidth > 1000) ? "UTM метки" : ""}
                    placeholder={(window.outerWidth > 1000) ? "" : "UTM метки"}
                    name="utm_markers"
                    onChange={values.handleChange}
                    value={values.values.utm_markers}
                    size={isMobile ? "small" : "medium"}
                  />
                  {Boolean(values.touched.utm_markers && values.errors.utm_markers) && (
                    <StyledHelperText>{values.errors.utm_markers}</StyledHelperText>
                  )}
                </FormControl>
                )}
              </Stack>
            </Stack>
          </div>
          {isMarkupEditor && (
            <div style={{ width: "100%" }}>
              <UtmMarkupForm
                value={data.utm_markers || ""}
                onChange={(next) => {
                  markupDraftRef.current = next;
                  setMarkupDraft(next);
                }}
                disabled={isContentLocked}
                landingPage={values.values.landing_page}
                campaignId={campaignId}
                bannerId={banners && banners.length > 0 ? banners[0].id : ""}
                error={values.touched.utm_markers && values.errors.utm_markers ? values.errors.utm_markers : ""}
                isMobile={isMobile}
                sourceDefault={signupDomain === RETARGEO_DOMAIN ? "Retargeo" : "Programmatic"}
              />
            </div>
          )}

          {
            ["ADMIN", "SUPERUSER"].some((el) => (el === role)) && (
              <div style={{ 
                display: "flex", 
                gap: isMobile ? "20px" : "30px",
                flexDirection: "column",
                marginBottom: "30px"
              }}>
                {/* Трекеры показов */}
                <Stack style={{ width: "100%" }}>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    marginBottom: isMobile ? "16px" : "20px"
                  }}>
                    <Typography variant="h1" style={{ fontSize: isMobile ? "16px" : undefined }}>
                      Трекеры показов
                    </Typography>
                    {(!values.values.event_trackers || values.values.event_trackers.length < 10) && (
                      <IconButton
                        disabled={isContentLocked}
                        onClick={() => {
                          values.setFieldValue("event_trackers", [...(values.values.event_trackers || []), ""]);
                        }}
                        style={{
                          color: (isContentLocked) ? "#ccc" : "#1976d2",
                          cursor: (isContentLocked) ? "not-allowed" : "pointer"
                        }}
                      >
                        <AddCircleOutlineIcon style={{ fontSize: isMobile ? "24px" : "28px" }} />
                      </IconButton>
                    )}
                  </div>
    
                  {values.values.event_trackers && values.values.event_trackers.length > 0 ? (
                    <Stack spacing={isMobile ? "12px" : "16px"}>
                      {values.values.event_trackers.map((tracker, index) => (
                        <div key={index} style={{ position: "relative" }}>
                          {isContentLocked && (
                            <div
                              onClick={() => enqueueSnackbar(lockMessage, { variant: "info" })}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                background: "transparent",
                                cursor: "not-allowed",
                                zIndex: 10,
                              }}
                            />
                          )}
                          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                            <FormControl fullWidth>
                              <TextField
                                disabled={isContentLocked}
                                label={window.outerWidth > 1000 ? `Трекер показа ${index + 1}` : ""}
                                placeholder={window.outerWidth > 1000 ? "https://example.com/impression" : `Трекер показа ${index + 1}`}
                                value={tracker}
                                onChange={(e) => {
                                  const newTrackers = [...values.values.event_trackers];
                                  newTrackers[index] = e.target.value;
                                  values.setFieldValue("event_trackers", newTrackers);
                                }}
                                onBlur={() => values.setFieldTouched(`event_trackers.${index}`, true)}
                                size={isMobile ? "small" : "medium"}
                                error={Boolean(getIn(values.errors, `event_trackers.${index}`))}
                                helperText={(() => { const e = getIn(values.errors, `event_trackers.${index}`); return typeof e === "string" ? e : (e && e.message) || ""; })()}
                              />
                            </FormControl>
                            <IconButton
                              disabled={isContentLocked}
                              onClick={() => {
                                const newTrackers = values.values.event_trackers.filter((_, i) => i !== index);
                                values.setFieldValue("event_trackers", newTrackers);
                              }}
                              style={{
                                color: (isContentLocked) ? "#ccc" : "#d32f2f",
                                marginTop: isMobile ? "4px" : "8px"
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: isMobile ? "20px" : "24px" }} />
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </Stack>
                  ) : (
                    <div style={{
                      padding: isMobile ? "20px" : "30px",
                      textAlign: "center",
                      color: "#999",
                      fontSize: isMobile ? "14px" : "16px",
                      border: "1px dashed #ddd",
                      borderRadius: "8px",
                      background: "#fafafa"
                    }}>
                      Нажмите на кнопку &quot;+&quot;, чтобы добавить трекер показа
                    </div>
                  )}
                </Stack>
    
                {/* Трекеры кликов */}
                <Stack style={{ width: "100%" }}>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    marginBottom: isMobile ? "16px" : "20px"
                  }}>
                    <Typography variant="h1" style={{ fontSize: isMobile ? "16px" : undefined }}>
                      Трекеры кликов
                    </Typography>
                    {(!values.values.click_trackers || values.values.click_trackers.length < 10) && (
                      <IconButton
                        disabled={isContentLocked}
                        onClick={() => {
                          values.setFieldValue("click_trackers", [...(values.values.click_trackers || []), ""]);
                        }}
                        style={{
                          color: (isContentLocked) ? "#ccc" : "#1976d2",
                          cursor: (isContentLocked) ? "not-allowed" : "pointer"
                        }}
                      >
                        <AddCircleOutlineIcon style={{ fontSize: isMobile ? "24px" : "28px" }} />
                      </IconButton>
                    )}
                  </div>
    
                  {values.values.click_trackers && values.values.click_trackers.length > 0 ? (
                    <Stack spacing={isMobile ? "12px" : "16px"}>
                      {values.values.click_trackers.map((tracker, index) => (
                        <div key={index} style={{ position: "relative" }}>
                          {isContentLocked && (
                            <div
                              onClick={() => enqueueSnackbar(lockMessage, { variant: "info" })}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                background: "transparent",
                                cursor: "not-allowed",
                                zIndex: 10,
                              }}
                            />
                          )}
                          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                            <FormControl fullWidth>
                              <TextField
                                disabled={isContentLocked}
                                label={window.outerWidth > 1000 ? `Трекер клика ${index + 1}` : ""}
                                placeholder={window.outerWidth > 1000 ? "https://example.com/click" : `Трекер клика ${index + 1}`}
                                value={tracker}
                                onChange={(e) => {
                                  const newTrackers = [...values.values.click_trackers];
                                  newTrackers[index] = e.target.value;
                                  values.setFieldValue("click_trackers", newTrackers);
                                }}
                                onBlur={() => values.setFieldTouched(`click_trackers.${index}`, true)}
                                size={isMobile ? "small" : "medium"}
                                error={Boolean(getIn(values.errors, `click_trackers.${index}`))}
                                helperText={(() => { const e = getIn(values.errors, `click_trackers.${index}`); return typeof e === "string" ? e : (e && e.message) || ""; })()}
                              />
                            </FormControl>
                            <IconButton
                              disabled={isContentLocked}
                              onClick={() => {
                                const newTrackers = values.values.click_trackers.filter((_, i) => i !== index);
                                values.setFieldValue("click_trackers", newTrackers);
                              }}
                              style={{
                                color: (isContentLocked) ? "#ccc" : "#d32f2f",
                                marginTop: isMobile ? "4px" : "8px"
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: isMobile ? "20px" : "24px" }} />
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </Stack>
                  ) : (
                    <div style={{
                      padding: isMobile ? "20px" : "30px",
                      textAlign: "center",
                      color: "#999",
                      fontSize: isMobile ? "14px" : "16px",
                      border: "1px dashed #ddd",
                      borderRadius: "8px",
                      background: "#fafafa"
                    }}>
                      Нажмите на кнопку &quot;+&quot;, чтобы добавить трекер клика
                    </div>
                  )}
                </Stack>
              </div>
            )
          }


        </div>

        
        <Stack>
          <div className="button_controls" style={isMobile ? {
            display: "flex",
            flexDirection: "row",
            gap: 10,
            marginTop: 20,
            justifyContent: "space-between",
            paddingBottom: 10
          } : {}}>
            <LoadingButton
              variant="contained"
              sx={{
                marginLeft: isMobile ? 0 : "32px",
                background: "#b82a2a",
                width: isMobile ? "auto" : "auto",
                minWidth: isMobile ? "100px" : "auto",
                fontSize: isMobile ? "12px" : "14px"
              }}
              disabled={hasImpressions}
              onClick={async () => {
                if (hasImpressions) {
                  enqueueSnackbar(LOCK_MSG_IMPRESSIONS, { variant: "info" });
                  return;
                }
                const confirm = window.confirm("Вы действительно хотите удалить объявление?")
                if (!confirm) return
                const myHeaders = new Headers();
                myHeaders.append("Content-Type", "application/json");
                myHeaders.append("Authorization", "Bearer " + localStorage.getItem("token"));
                const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/delete?_id=${currentAdId}`, {
                  method: "POST",
                  body: JSON.stringify({}),
                  headers: myHeaders,
                });
                if (!response.ok) {
                  let errorMessage = LOCK_MSG_IMPRESSIONS;
                  try {
                    const errorData = await response.json();
                    if (errorData?.detail) errorMessage = String(errorData.detail);
                  } catch (_) {}
                  enqueueSnackbar(errorMessage, { variant: "error" });
                  return;
                }
                setUpd((value) => !value)
                enqueueSnackbar(`Объявление успешно удалено`, { variant: "success" });
                if (inModal && onClose) onClose(true);
              }}
            >
              {"Удалить"}
            </LoadingButton>
            <LoadingButton
              loading={isSubmitting}
              loadingPosition="end"
              variant="contained"
              disabled={isContentLocked}
              onClick={() => submitAdvertisement()}
              sx={{
                width: isMobile ? "auto" : "auto",
                minWidth: isMobile ? "120px" : "auto",
                fontSize: isMobile ? "12px" : "14px"
              }}
            >
              {isSubmitting ? "Сохранение" : "Сохранить"}
            </LoadingButton>
            {inModal && onClose && (
              <LoadingButton
                variant="outlined"
                onClick={() => onClose(false)}
                sx={{
                  marginLeft: isMobile ? 0 : "16px",
                  fontSize: isMobile ? "12px" : "14px",
                }}
              >
                Закрыть
              </LoadingButton>
            )}
            {!hideNextButton && !inModal && <div style={{ width: isMobile ? "auto" : "auto" }}>
              <LoadingButton
                loadingPosition="end"
                variant="contained"
                sx={{
                  marginLeft: isMobile ? 0 : "32px",
                  width: isMobile ? "auto" : "auto",
                  minWidth: isMobile ? "80px" : "auto",
                  fontSize: isMobile ? "12px" : "14px"
                }}
                onClick={async () => {
                  await submitAdvertisement();
                  if (values.isValid) {
                    if (!user.is_trial || user.is_trial === false) {
                      changeTab("event", "4")
                    } else {
                      changeTab("event", "5")
                    }
                  }
                }}
                endIcon={<SendIcon />}
              >
                Далее
              </LoadingButton>
            </div>}
          </div>
        </Stack>
      </form>
    </div>
  );
}